// ============================================
// NOTIFICATION DROPDOWN
// ============================================

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Bell,
  Check,
  Loader2,
  Briefcase,
  Award,
  FileText,
  AlertTriangle,
  Star,
  X,
} from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useNotifications, useUnreadNotificationCount, useMarkNotificationAsRead, useMarkAllNotificationsAsRead } from '@/hooks';
import { formatRelativeTime, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export function NotificationDropdown() {
  const { t } = useTranslation('panel');
  const [isOpen, setIsOpen] = useState(false);
  const { data: unreadData } = useUnreadNotificationCount();
  const { data: notificationsResponse, isLoading } = useNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications: any[] = (notificationsResponse as any)?.data || notificationsResponse || [];
  const unreadCount = unreadData?.count ?? notifications.filter((n: any) => !n.isRead).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'application_received':
      case 'application_status_changed':
      case 'application_shortlisted':
      case 'application_rejected':
        return Briefcase;
      case 'job_match':
      case 'new_job_match':
        return Star;
      case 'certificate_expiring':
      case 'certificate_expired':
        return AlertTriangle;
      case 'badge_awarded':
        return Award;
      case 'verification_status':
        return FileText;
      default:
        return Bell;
    }
  };

  const handleMarkAsRead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <Popover.Trigger asChild>
        <button
          className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={t('notificationDropdown.title')}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-red-500 rounded-full">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          sideOffset={5}
          align="end"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">{t('notificationDropdown.title')}</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsRead.isPending}
                  className="text-xs text-maritime-600 hover:text-maritime-700 font-medium disabled:opacity-50"
                >
                  {markAllAsRead.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    t('notificationDropdown.markAllRead')
                  )}
                </button>
              )}
              <Popover.Close asChild>
                <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                  <X className="w-4 h-4" />
                </button>
              </Popover.Close>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-maritime-600" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">{t('notificationDropdown.empty')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 10).map((notification: any) => {
                  const Icon = getNotificationIcon(notification.type);
                  return (
                    <div
                      key={notification.id}
                      className={cn(
                        'px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors cursor-pointer',
                        !notification.isRead && 'bg-maritime-50/50'
                      )}
                    >
                      <div
                        className={cn(
                          'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0',
                          !notification.isRead ? 'bg-maritime-100' : 'bg-gray-100'
                        )}
                      >
                        <Icon
                          className={cn(
                            'w-4 h-4',
                            !notification.isRead ? 'text-maritime-600' : 'text-gray-500'
                          )}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            'text-sm line-clamp-2',
                            !notification.isRead ? 'font-medium text-gray-900' : 'text-gray-600'
                          )}
                        >
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatRelativeTime(notification.createdAt)}
                        </p>
                      </div>
                      {!notification.isRead && (
                        <button
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          disabled={markAsRead.isPending}
                          className="p-1.5 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-full flex-shrink-0"
                          title={t('notificationDropdown.markRead')}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm text-maritime-600 hover:text-maritime-700 font-medium"
              >
                {t('notificationDropdown.viewAll')}
              </Link>
            </div>
          )}

          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
